import { useEffect, useState, type ReactNode } from 'react';
import { AudioBufferCache, browserDecodeDeps } from '../audio/assets/bufferCache';
import { AudioEngine } from '../audio/audioEngine';
import { AVSessionController } from '../av/sessionController';
import { WavExporter, triggerDownload } from '../audio/wavExporter';
import { VideoExporter } from '../video/videoExporter';
import { AnalysisStore } from '../visualizer/analysisStore';
import { needsAnalysis } from '../visualizer/evaluate';
import { MAX_OUTPUT_BYTES, MAX_REMUX_BYTES } from '../video/frames';
import { createDefaultRegistry } from '../providers/registry';
import { openDatabase } from '../storage/db';
import { ProjectRepository } from '../storage/projectRepository';
import { RecipeRepository } from '../storage/recipeRepository';
import { debugCounters } from '../lib/debug';
import { AnimationBus } from './animationBus';
import { MotionComposerStore } from './motionComposer';
import { SelectionStore } from './selection';
import { SyncSelectionStore } from './syncSelection';
import { ServicesContext, type Services } from './services';
import { Observable } from '../lib/store';
import type { UnsavedWork } from './recovery';

function createServices(): Services {
  let db: Promise<IDBDatabase> | null = null;
  const getDb = () => {
    db ??= openDatabase(undefined, undefined, () => {
      db = null; // another tab upgraded the schema: reopen on next use
    }).catch((error) => {
      db = null; // allow a retry after e.g. a blocked upgrade
      throw error;
    });
    return db;
  };
  const repo = new ProjectRepository(getDb);
  const buffers = new AudioBufferCache(
    browserDecodeDeps(async (assetId) => {
      const blob = await repo.getBlob(assetId);
      return blob && { data: blob.data, mimeType: blob.mimeType };
    }),
  );
  const audio = new AudioEngine(undefined, buffers);
  debugCounters.audioStats = () => {
    const s = buffers.stats();
    return { cacheBytes: s.bytes, cacheEntries: s.entries, decodes: s.decodes, evictions: s.evictions, liveVoices: audio.liveVoices() };
  };
  const images = {
    async load(assetId: string) {
      const blob = await repo.getBlob(assetId);
      if (!blob) throw new Error('The image data is missing from this browser’s storage.');
      return createImageBitmap(new Blob([blob.data], { type: blob.mimeType }));
    },
  };
  const analysis = new AnalysisStore(buffers);
  return {
    repo,
    registry: createDefaultRegistry(),
    audio,
    exporter: new WavExporter(undefined, undefined, buffers),
    animation: new AnimationBus(),
    buffers,
    images,
    selection: new SelectionStore(),
    motion: new MotionComposerStore(),
    recipes: new RecipeRepository(getDb),
    sync: new AVSessionController(audio, analysis),
    analysis,
    recovery: new Observable<UnsavedWork | null>(null),
    syncSelection: new SyncSelectionStore(),
    videoExporter: new VideoExporter({
      loadImage: images.load,
      audio: buffers,
      download: triggerDownload,
      // Test overrides can only lower the limits.
      maxOutputBytes: () => Math.min(debugCounters.videoOutputLimit ?? MAX_OUTPUT_BYTES, MAX_OUTPUT_BYTES),
      maxRemuxBytes: () => Math.min(debugCounters.videoRemuxLimit ?? MAX_REMUX_BYTES, MAX_REMUX_BYTES),
      analysis: (snapshot, signal) => (needsAnalysis(snapshot.session) ? analysis.ensure(snapshot.project, snapshot.session, signal, { retry: true }) : Promise.resolve(null)),
    }),
  };
}

export function ServicesProvider({ children, services }: { children: ReactNode; services?: Services }) {
  const [value] = useState(() => services ?? createServices());
  useEffect(() => {
    return () => {
      value.sync.dispose();
      void value.audio.dispose();
      value.exporter.dispose();
      value.videoExporter.dispose();
      value.analysis.clear();
      value.buffers.clear();
    };
  }, [value]);
  return <ServicesContext.Provider value={value}>{children}</ServicesContext.Provider>;
}
